import { useState } from 'react';
import printingService, { printErrorMessage, type SeedDefaultsPlan } from '../../services/printing.service';

const PREVIEW_ROWS = 50;

/**
 * Application Settings card: copy each user's V1 default printers (gwi.users
 * default_printer / default_label) into 360 user_settings, matched on v1_username.
 * Preview first, then Apply. Users who already chose a printer in 360 are left alone,
 * and dead or wrong-type queues are never copied. Re-running converges.
 */
export default function PrinterDefaultsSeedPanel() {
  const [plan, setPlan] = useState<SeedDefaultsPlan | null>(null);
  const [busy, setBusy] = useState<'preview' | 'apply' | null>(null);
  const [error, setError] = useState('');
  const [done, setDone] = useState('');

  async function preview() {
    setBusy('preview'); setError(''); setDone(''); setPlan(null);
    try { setPlan(await printingService.seedDefaultsPlan()); }
    catch (e) { setError(printErrorMessage(e)); }
    finally { setBusy(null); }
  }

  async function apply() {
    setBusy('apply'); setError('');
    try {
      const r = await printingService.seedDefaultsApply();
      setDone(`Written: ${r.written.toLocaleString()} (${r.lasers.toLocaleString()} laser, ${r.labels.toLocaleString()} label).`);
      setPlan(await printingService.seedDefaultsPlan());
    } catch (e) { setError(printErrorMessage(e)); }
    finally { setBusy(null); }
  }

  const skipped = plan ? Object.entries(plan.skipped_inactive) : [];

  return (
    <section className="bg-panel shadow rounded-lg p-6 mt-6">
      <h2 className="text-lg font-medium text-heading mb-3">Default printers from V1</h2>
      <p className="text-sm text-muted mb-4">
        Copies each user&apos;s V1 default laser and label printer into their 360 settings, matched on
        V1 username. Anyone who already picked a printer in 360 is left alone, and printers that are
        deleted or the wrong type are skipped. Safe to run more than once.
      </p>
      <div className="flex flex-wrap gap-3">
        <button type="button" disabled={!!busy} onClick={preview}
          className="px-4 py-2 rounded bg-blue-600 text-white disabled:opacity-50">
          {busy === 'preview' ? 'Loading…' : 'Preview import'}
        </button>
        {plan && plan.to_write > 0 && (
          <button type="button" disabled={!!busy} onClick={apply}
            className="px-4 py-2 rounded bg-green-600 text-white disabled:opacity-50">
            {busy === 'apply' ? 'Applying…' : `Apply ${plan.to_write.toLocaleString()} defaults`}
          </button>
        )}
      </div>

      {plan && (
        <div role="status" className="mt-4 text-sm text-label space-y-1">
          <p>{plan.users.toLocaleString()} V1 users with a default and a 360 account.</p>
          <p>{plan.active_laser} active laser printers, {plan.active_label} active label printers.</p>
          <p>{plan.skipped_existing.toLocaleString()} already set in 360, left alone.</p>
          <p>{skipped.reduce((n, [, c]) => n + c, 0).toLocaleString()} skipped because the printer is inactive or the wrong type.</p>
          <p className="font-medium">
            To write: {plan.to_write.toLocaleString()} ({plan.lasers.toLocaleString()} laser, {plan.labels.toLocaleString()} label).
          </p>
          {skipped.length > 0 && (
            <details className="mt-2">
              <summary className="cursor-pointer">Skipped printer names</summary>
              <ul className="mt-1 ml-4 list-disc">
                {skipped.map(([name, n]) => <li key={name}>{name} ×{n}</li>)}
              </ul>
            </details>
          )}
          {plan.items.length > 0 && (
            <details className="mt-2">
              <summary className="cursor-pointer">
                Rows to write{plan.items.length > PREVIEW_ROWS ? ` (first ${PREVIEW_ROWS} of ${plan.items.length.toLocaleString()})` : ''}
              </summary>
              <table className="mt-2 text-sm">
                <thead><tr className="text-left"><th className="pr-4">User</th><th className="pr-4">Setting</th><th>Printer</th></tr></thead>
                <tbody>
                  {plan.items.slice(0, PREVIEW_ROWS).map((it) => (
                    <tr key={`${it.email}|${it.key}`}>
                      <td className="pr-4">{it.email}</td>
                      <td className="pr-4">{it.key === 'print.default_laser' ? 'Laser' : 'Label'}</td>
                      <td>{it.printer}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          )}
        </div>
      )}
      {done && <p className="mt-3 text-sm text-green-700 dark:text-green-400">{done}</p>}
      {error && <p role="alert" className="mt-3 text-sm text-red-600">{error}</p>}
    </section>
  );
}
